import { useMemo } from "react";
import {
  buildAndroidChromeIntentUrl,
  detectDownloadSupport,
  isIOSPlatformUA,
  type BrowserHint,
} from "@/lib/utils/browserDetect";

interface UseAllowedUserAgentReturn {
  isAllowed: boolean;
  isDownloadRestricted: boolean;
  browserHint: BrowserHint | null;
  openInBrowserProps: {
    onClick: () => void;
    href: string;
    label: string;
  } | null;
}

/** TikTok WebView UAs often mimic Chrome/Safari and omit "tiktok". */
const isTikTokInAppBrowser = (userAgent: string): boolean =>
  userAgent.includes("tiktok") ||
  userAgent.includes("musical_ly") ||
  userAgent.includes("trill_") ||
  userAgent.includes("trill/") ||
  userAgent.includes("bytedancewebview") ||
  userAgent.includes("appname/trill") ||
  userAgent.includes("appname/musical_ly") ||
  userAgent.includes("aweme");

/**
 * Hook to check if the user is on an allowed browser
 * @returns Object with isAllowed boolean and openInBrowserProps for deep linking to default browser
 */
export const useAllowedUserAgent = (): UseAllowedUserAgentReturn => {
  const result = useMemo(() => {
    // Server-side rendering guard
    if (typeof window === "undefined" || !window.navigator) {
      return {
        isAllowed: true,
        isDownloadRestricted: false,
        browserHint: null,
        openInBrowserProps: null,
      };
    }

    const userAgent = window.navigator.userAgent.toLowerCase();
    const currentUrl = window.location.href;

    // Check for each browser (order matters!)
    // Edge (Chromium-based) - check first as it contains "chrome"
    const isEdge = userAgent.includes("edg/") || userAgent.includes("edge/");
    
    // Opera - check before Chrome as it contains "chrome"
    const isOpera = userAgent.includes("opr/") || userAgent.includes("opera");
    
    // Chrome - check before Safari as it contains "safari"
    const isChrome = userAgent.includes("chrome") && !isEdge && !isOpera;
    
    // Safari - check after Chrome
    const isSafari = userAgent.includes("safari") && !isChrome && !isEdge && !isOpera;
    
    // Firefox
    const isFirefox = userAgent.includes("firefox");
    // Detect common in-app browsers for better messaging
    const isInstagram = userAgent.includes("instagram");
    const isFacebook = userAgent.includes("fb") || userAgent.includes("facebook");
    const isLine = userAgent.includes("line");
    const isTikTok = isTikTokInAppBrowser(userAgent);
    const isTwitter = userAgent.includes("twitter");

    // Google app / Android WebView are NOT hard-blocked: they are allowed,
    // but flagged as download-restricted (programmatic downloads fail there).
    const { isDownloadRestricted, browserHint } =
      detectDownloadSupport(userAgent);

    const isInAppBrowser = isInstagram || isFacebook || isLine || isTikTok || isTwitter;
    
    const isAllowed = (isEdge || isOpera || isChrome || isSafari || isFirefox) && !isInAppBrowser;

    // Shared iOS attempt: x-safari-https scheme, then a plain window.open fallback.
    const tryOpenInSafari = () => {
      window.location.href = currentUrl.replace(/^https?:\/\//, "x-safari-https://");

      // Fallback: just open the URL (will prompt user to choose browser)
      setTimeout(() => {
        window.open(currentUrl, "_blank");
      }, 500);
    };

    // Hard-blocked in-app browsers: unchanged behavior — Android gets the
    // browser chooser via window.open("_system"), which works regardless of
    // how the host app handles intent:// schemes.
    const handleOpenInBrowser = () => {
      if (isIOSPlatformUA(userAgent)) {
        tryOpenInSafari();
      } else {
        window.open(currentUrl, "_system");
      }
    };

    // Download-restricted but allowed browsers (Google app / Android WebView):
    // force Chrome via intent:// URL, with a browser-chooser fallback.
    const handleOpenInExternalBrowser = () => {
      if (isIOSPlatformUA(userAgent)) {
        tryOpenInSafari();
      } else {
        window.location.href = buildAndroidChromeIntentUrl(currentUrl);
      }
    };

    // If not allowed, provide props to open in default browser
    if (!isAllowed) {
      return {
        isAllowed: false,
        isDownloadRestricted,
        browserHint,
        openInBrowserProps: {
          onClick: handleOpenInBrowser,
          href: currentUrl,
          label: "Go to Website",
        },
      };
    }

    // Download-restricted browsers (Google app / Android WebView) are allowed,
    // but get an explicit "Open in Browser" escape hatch for downloads.
    if (isDownloadRestricted) {
      return {
        isAllowed: true,
        isDownloadRestricted: true,
        browserHint,
        openInBrowserProps: {
          onClick: handleOpenInExternalBrowser,
          href: currentUrl,
          label: "Open in Browser",
        },
      };
    }

    return {
      isAllowed: true,
      isDownloadRestricted: false,
      browserHint: null,
      openInBrowserProps: null,
    };
  }, []);

  return result;
};
